import type {
  Balance,
  SpendResponse,
  EarnResponse,
  EntitlementsResponse,
  ExperienceMechanicsResponse,
  SpendMechanic,
  MintTokenResponse,
  ApiError,
} from '@knockonwood/arcade-card-shared';
import { ArcadeCardError } from './errors.js';

export { ArcadeCardError, ArcadeCardError as ArcadePassError };
export type { Balance, ExperienceMechanicsResponse, SpendMechanic };
export { ArcadePlay } from './play.js';
export type { ArcadePlayConfig } from './play.js';
export { createRedirectTokenProvider } from './redirect-auth.js';
export type { RedirectTokenProviderConfig } from './redirect-auth.js';

/** A function the host page supplies to obtain/refresh an experience token. */
export type TokenProvider = () => Promise<MintTokenResponse>;

export interface ArcadePassConfig {
  /** Catalog id of this game, app, or world (e.g. "cold-light"). */
  experienceId?: string;
  /** @deprecated Use `experienceId`. Kept for existing game integrations. */
  gameId?: string;
  /** Cashier base URL. Defaults to the current origin. */
  apiBase?: string;
  /**
   * Central studio-site origin used by `loginUrl()` and `reloadUrl()`.
   * Defaults by removing an `api.` hostname prefix from `apiBase`.
   */
  siteBase?: string;
  /**
   * How to get an experience token. Omit for the same-site default (the SDK
   * calls `POST /v1/experiences/:experienceId/token` with the studio session).
   * For a standalone cross-site world, use `createRedirectTokenProvider`.
   */
  getToken?: TokenProvider;
}

/** @deprecated Prefer `ArcadePassConfig`. */
export type ArcadeCardConfig = ArcadePassConfig;

type BalanceListener = (b: Balance) => void;

/**
 * Thin, framework-agnostic client. Holds NO balance authority, NO prices, NO
 * secrets — it's a typed wrapper over the cashier API plus token plumbing.
 * The server prices every spend and owns the ledger.
 */
export class ArcadePass {
  private experienceId!: string;
  private apiBase!: string;
  private siteBase!: string;
  private getToken!: TokenProvider;

  private token: string | null = null;
  private tokenExpiresAt = 0; // epoch ms
  private balance: Balance | null = null;
  private readonly listeners = new Set<BalanceListener>();
  private initialized = false;

  /** Resolve a token and prime the balance. Call once before anything else. */
  async init(config: ArcadePassConfig): Promise<void> {
    const experienceId = config.experienceId ?? config.gameId;
    if (!experienceId) {
      throw new ArcadeCardError(
        'NOT_INITIALIZED',
        'experienceId is required to initialize the Arcade Pass',
      );
    }
    this.experienceId = experienceId;
    this.apiBase = (config.apiBase ?? globalThis.location?.origin ?? '').replace(/\/$/, '');
    this.siteBase = (config.siteBase ?? inferSiteBase(this.apiBase)).replace(/\/$/, '');
    this.getToken = config.getToken ?? this.defaultTokenProvider();
    await this.refreshToken();
    this.initialized = true;
    await this.getBalance(); // primes + emits
  }

  async getBalance(): Promise<Balance> {
    const balance = await this.request<Balance>('GET', '/v1/wallet/balance');
    this.setBalance(balance);
    return balance;
  }

  /**
   * Return the current experience's server-owned spend/reward definitions and
   * their affordability against this account's latest balance.
   */
  async getMechanics(): Promise<ExperienceMechanicsResponse> {
    const mechanics = await this.request<ExperienceMechanicsResponse>(
      'GET',
      '/v1/wallet/mechanics',
    );
    this.setBalance(mechanics.balance);
    return mechanics;
  }

  /** Find one spend mechanic by its stable catalog id. */
  async getMechanic(id: string): Promise<SpendMechanic | null> {
    const mechanics = await this.getMechanics();
    return mechanics.spend.find((mechanic) => mechanic.id === id) ?? null;
  }

  /** Check a quantity against the current server-priced mechanic and balance. */
  async canAfford(id: string, quantity = 1): Promise<boolean> {
    if (!Number.isSafeInteger(quantity) || quantity < 1) {
      throw new ArcadeCardError('INVALID_AMOUNT', 'quantity must be a positive integer');
    }
    const mechanics = await this.getMechanics();
    const mechanic = mechanics.spend.find((entry) => entry.id === id);
    if (!mechanic) return false;
    return mechanic.cost * quantity <= mechanics.balance.total;
  }

  /**
   * Debit by SKU. The server prices it. An idempotency key is generated and
   * REUSED across retries of the same logical spend, so a network retry can't
   * double-charge. Rejects with INSUFFICIENT_FUNDS / UNKNOWN_SKU / etc.
   */
  async spend(
    sku: string,
    opts: { quantity?: number; idempotencyKey?: string } = {},
  ): Promise<SpendResponse> {
    const idempotencyKey = opts.idempotencyKey ?? randomId();
    const res = await this.request<SpendResponse>(
      'POST',
      '/v1/wallet/spend',
      { sku, quantity: opts.quantity ?? 1 },
      { 'Idempotency-Key': idempotencyKey },
    );
    this.setBalance(res.balance);
    return res;
  }

  /** Product-language alias for executing a credit-consuming mechanic. */
  use(
    mechanicId: string,
    opts: { quantity?: number; idempotencyKey?: string } = {},
  ): Promise<SpendResponse> {
    return this.spend(mechanicId, opts);
  }

  /** Request a grant for a server-validated reason (server enforces caps). */
  async earn(
    reason: string,
    opts: { onceValue?: string; proof?: string; idempotencyKey?: string } = {},
  ): Promise<EarnResponse> {
    const res = await this.request<EarnResponse>(
      'POST',
      '/v1/wallet/earn',
      { reason, onceValue: opts.onceValue, proof: opts.proof },
      { 'Idempotency-Key': opts.idempotencyKey ?? randomId() },
    );
    this.setBalance(res.balance);
    return res;
  }

  /** Product-language alias for claiming a verified reward mechanic. */
  claim(
    mechanicId: string,
    opts: { onceValue?: string; proof?: string; idempotencyKey?: string } = {},
  ): Promise<EarnResponse> {
    return this.earn(mechanicId, opts);
  }

  /** Persistent unlocks/cosmetics for this game. */
  async entitlements(): Promise<string[]> {
    const res = await this.request<EntitlementsResponse>(
      'GET',
      '/v1/wallet/entitlements',
    );
    return res.entitlements;
  }

  /** Subscribe to balance changes (reload/spend/earn). Returns an unsubscribe. */
  on(event: 'balanceChange', cb: BalanceListener): () => void {
    if (event !== 'balanceChange') return () => {};
    this.listeners.add(cb);
    if (this.balance) cb(this.balance);
    return () => this.listeners.delete(cb);
  }

  /** Build a central login URL that resumes at the calling experience. */
  loginUrl(returnTo = globalThis.location?.href): string {
    return this.studioUrl('/login', returnTo);
  }

  /** Build a central credit-reload URL that returns to the calling experience. */
  reloadUrl(returnTo = globalThis.location?.href): string {
    return this.studioUrl('/reload', returnTo);
  }

  /** Navigate to the central login experience. */
  openLogin(returnTo = globalThis.location?.href): void {
    navigate(this.loginUrl(returnTo));
  }

  /** Navigate to the central reload experience. */
  openReload(returnTo = globalThis.location?.href): void {
    navigate(this.reloadUrl(returnTo));
  }

  // --- internals -------------------------------------------------------------

  private defaultTokenProvider(): TokenProvider {
    return async () => {
      const res = await fetch(`${this.apiBase}/v1/experiences/${encodeURIComponent(this.experienceId)}/token`, {
        method: 'POST',
        credentials: 'include', // send the site-session cookie
      });
      if (!res.ok) {
        throw new ArcadeCardError(
          'UNAUTHORIZED',
          'Could not mint an experience token (not signed in?)',
          res.status,
        );
      }
      return (await res.json()) as MintTokenResponse;
    };
  }

  private async refreshToken(): Promise<void> {
    const { token, expiresIn } = await this.getToken();
    this.token = token;
    this.tokenExpiresAt = Date.now() + expiresIn * 1000;
  }

  private async ensureFreshToken(): Promise<void> {
    // Refresh ~30s before expiry to avoid mid-call expiration.
    if (!this.token || Date.now() >= this.tokenExpiresAt - 30_000) {
      await this.refreshToken();
    }
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    headers: Record<string, string> = {},
  ): Promise<T> {
    if (!this.initialized && path !== '/v1/wallet/balance') {
      throw new ArcadeCardError('NOT_INITIALIZED', 'Call init() before using the card');
    }
    await this.ensureFreshToken();

    const doFetch = async (): Promise<Response> => {
      const h: Record<string, string> = { Authorization: `Bearer ${this.token}`, ...headers };
      const init: RequestInit = { method, headers: h };
      if (body !== undefined) {
        h['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
      }
      try {
        return await fetch(`${this.apiBase}${path}`, init);
      } catch (e) {
        throw new ArcadeCardError('NETWORK', (e as Error).message || 'Network error');
      }
    };

    let res = await doFetch();
    // One transparent retry on auth expiry (idempotency key is stable, so a
    // retried spend can't double-charge).
    if (res.status === 401) {
      await this.refreshToken();
      res = await doFetch();
    }
    if (!res.ok) {
      const err = (await res.json().catch(() => null)) as ApiError | null;
      throw new ArcadeCardError(err?.code ?? 'INTERNAL', err?.message ?? `HTTP ${res.status}`, res.status);
    }
    return (await res.json()) as T;
  }

  private setBalance(b: Balance): void {
    this.balance = b;
    for (const cb of this.listeners) cb(b);
  }

  private studioUrl(path: string, returnTo?: string): string {
    if (!this.siteBase) {
      throw new ArcadeCardError('NOT_INITIALIZED', 'Call init() before requesting site URLs');
    }
    const url = new URL(path, `${this.siteBase}/`);
    if (returnTo) url.searchParams.set('return', returnTo);
    return url.toString();
  }
}

/** Backward-compatible class name for existing game integrations. */
export { ArcadePass as ArcadeCard };

function randomId(): string {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function inferSiteBase(apiBase: string): string {
  try {
    const url = new URL(apiBase);
    if (url.hostname.startsWith('api.')) url.hostname = url.hostname.slice(4);
    return url.origin;
  } catch {
    return apiBase;
  }
}

function navigate(url: string): void {
  if (!globalThis.location?.assign) {
    throw new ArcadeCardError('NETWORK', 'Browser navigation is unavailable');
  }
  globalThis.location.assign(url);
}
